import * as React from "react";
import { FindEquipmentAdditionalFilter } from "../../../components/FindEquipmentAdditionalFilter";
import { AppTranslations } from "../../../../../../components/Utils/translations/AppTranslations";
import {
	AutoSuggestionItemProps,
	InputAddon,
	Spinner,
	SpinnerSize,
	TextInput,
	useDebounce,
} from "@vebox/etui-core";
import {
	INPUT_BORDER_BOX,
	Styles,
} from "../../../../../../components/common/etui_components_styles";
import { FindEquipmentContext } from "../../../providers/FindEquipmentProvider";
import { ADDITIONAL_FILTERS } from "../../../types";
import { useEquipmentModel } from "../../../data/hooks/CecnsusCoolerControllerHooks";
import {
	createKeyFilter,
	createModelFilter,
	mapEquipmentModelsIntoAutoSuggestions,
} from "./utils";
import VeBoxAutoSuggest from "../../../components/VeBoxAutoSuggest";

declare var AppTranslations: AppTranslations;

const INPUT_BORDER_BOX_WITH_MARGIN: Styles = {
	...INPUT_BORDER_BOX,
	root: {
		mb: 10,
	},
	input: {
		...INPUT_BORDER_BOX.input,
		borderRadius: 3,
	},
};

interface FeaturesFilterProps {
	open?: boolean;
}

export const FeaturesFilter: React.FC<FeaturesFilterProps> = ({ open }) => {
	const {
		additionalVeBoxFilters,
		handleVeBoxAdditionalFilters,
		isRemovingAll,
	} = React.useContext(FindEquipmentContext);

	const [keyFilter, setKeyFilter] = React.useState("");
	const [modelNameValue, setModelNameValue] = React.useState("");
	const [partialModelName, setPartialModelName] = useDebounce(undefined, 500);
	const [selectedModelName, setSelectedModelName] =
		React.useState<AutoSuggestionItemProps>(undefined);
	const [fetchSuggestions, setFetchSuggestions] = React.useState<boolean>(true);
	const [canOpen, setCanOpen] = React.useState<boolean>(false);

	const equipmentModels = useEquipmentModel(
		partialModelName && fetchSuggestions,
		partialModelName,
	);
	const suggestions = React.useMemo(
		() =>
			mapEquipmentModelsIntoAutoSuggestions(equipmentModels?.data?.list ?? []),
		[equipmentModels?.data?.list],
	);

	const isLoadingEquipmentModels = React.useMemo(
		() => equipmentModels?.isLoading || equipmentModels?.isValidating,
		[equipmentModels?.isLoading, equipmentModels?.isValidating],
	);

	const isActive = React.useMemo(
		() => Boolean(keyFilter) || Boolean(modelNameValue),
		[keyFilter, modelNameValue],
	);

	//Key
	const handleCodeValueChange: React.ChangeEventHandler<HTMLInputElement> = (
		e,
	) => {
		setKeyFilter(e.target.value);
		if (e.target.value === "") {
			clearCodeValue(true);
		}
	};
	const clearCodeValue = (propagate?: boolean) => {
		setKeyFilter("");
		if (propagate) {
			handleVeBoxAdditionalFilters(
				"remove",
				createKeyFilter(additionalVeBoxFilters?.equipmentFiltersCategory?.code),
				ADDITIONAL_FILTERS.KEY,
			);
		}
	};

	//Model
	const handleModelValueChange = (value: string) => {
		setPartialModelName(value);
		setCanOpen(false);
		setModelNameValue(value);
		if (value === "") {
			clearModelValue(true);
		}
		if (
			selectedModelName &&
			value === selectedModelName?.label &&
			fetchSuggestions
		) {
			setFetchSuggestions(false);
		}
		if (
			selectedModelName &&
			value !== selectedModelName?.label &&
			!fetchSuggestions
		) {
			setFetchSuggestions(true);
		}
	};
	const onSelectedValueChange = (suggestion: AutoSuggestionItemProps) => {
		setFetchSuggestions(false);
		setSelectedModelName(suggestion);
		handleVeBoxAdditionalFilters(
			"add",
			createModelFilter([suggestion?.value]),
			ADDITIONAL_FILTERS.COMPONENT_MODEL,
		);
	};
	const renderItemToString = (item: AutoSuggestionItemProps) => {
		return item?.label ?? "";
	};
	const clearModelValue = (propagate?: boolean) => {
		if (selectedModelName && propagate) {
			handleVeBoxAdditionalFilters(
				"remove",
				createModelFilter([selectedModelName?.value]),
				ADDITIONAL_FILTERS.COMPONENT_MODEL,
			);
		}
		setSelectedModelName(undefined);
		setModelNameValue("");
		setPartialModelName("");
	};

	const renderTextInputAddon = () => {
		return (
			<>
				{Boolean(keyFilter) ? (
					<InputAddon position={"end"}>
						<span
							className="material-icons-outlined clickable"
							onClick={() => clearCodeValue(true)}
						>
							clear
						</span>
					</InputAddon>
				) : null}
			</>
		);
	};

	const renderAutoSuggestInputAddon = () => {
		if (isLoadingEquipmentModels) {
			return (
				<InputAddon position="end">
					<Spinner size={SpinnerSize.xSmall} />
				</InputAddon>
			);
		} else if (partialModelName && selectedModelName) {
			return (
				<InputAddon position="end">
					<span
						className="material-icons-outlined clickable"
						onClick={() => clearModelValue(true)}
					>
						clear
					</span>
				</InputAddon>
			);
		}
		return null;
	};

	React.useEffect(() => {
		if (isLoadingEquipmentModels) {
			setCanOpen(true);
		}
	}, [isLoadingEquipmentModels]);

	React.useEffect(() => {
		if (keyFilter === undefined) {
			return;
		}
		if (keyFilter === "") {
			handleVeBoxAdditionalFilters(
				"remove",
				createKeyFilter(keyFilter),
				ADDITIONAL_FILTERS.KEY,
			);
		} else {
			handleVeBoxAdditionalFilters(
				"add",
				createKeyFilter(keyFilter),
				ADDITIONAL_FILTERS.KEY,
			);
		}
	}, [keyFilter]);

	React.useEffect(() => {
		if (isRemovingAll) {
			clearModelValue();
			clearCodeValue();
		}
	}, [isRemovingAll]);

	return (
		<FindEquipmentAdditionalFilter
			name={AppTranslations.filters.equipment.featuresTitle}
			active={isActive}
			open={open}
			openIfActive={true}
		>
			<TextInput
				label={AppTranslations.filters.equipment.equipmentCode}
				onChange={handleCodeValueChange}
				styles={INPUT_BORDER_BOX_WITH_MARGIN as any}
				defaultValue={keyFilter}
				value={keyFilter}
				endInputAddon={renderTextInputAddon()}
			/>
			<VeBoxAutoSuggest
				label={AppTranslations.filters.equipment.equipmentModel}
				styles={INPUT_BORDER_BOX_WITH_MARGIN as any}
				emptyPlaceholder={AppTranslations.noDataAvailable}
				suggestions={suggestions}
				itemToString={renderItemToString}
				onInputValueChange={handleModelValueChange}
				inputValue={modelNameValue}
				onSelectedValueChange={onSelectedValueChange}
				selectedItem={selectedModelName}
				excludeSelected={true}
				openOnFocus={false}
				closeOnLoading={true}
				endInputAddon={renderAutoSuggestInputAddon()}
				isLoading={isLoadingEquipmentModels}
				canOpen={canOpen || suggestions?.length > 0}
			/>
		</FindEquipmentAdditionalFilter>
	);
};
